<template>
  <div class="mod-page proyecto-page">
    <!-- Header -->
    <div class="mod-header">
      <div>
        <h1 class="mod-title">{{ titulo }}</h1>
        <p class="mod-subtitle">{{ subtitulo }}</p>
      </div>
      <div class="mod-header-actions">
        <button class="mod-btn mod-btn-outline" @click="exportExcel">
          <i class="bi bi-file-earmark-excel"></i>
          <span>Exportar</span>
        </button>
        <button class="mod-btn mod-btn-outline" @click="sincronizarErp" :disabled="syncing">
          <span v-if="syncing" class="mod-spinner"></span>
          <i v-else class="bi bi-arrow-repeat"></i>
          <span>{{ syncing ? 'Sincronizando...' : 'Sincronizar ERP' }}</span>
        </button>
        <button class="mod-btn mod-btn-primary" @click="openForm()">
          <i class="bi bi-plus-lg"></i>
          <span>Nuevo proyecto</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="mod-filters proyecto-filters">
      <div class="mod-search">
        <i class="bi bi-search"></i>
        <input
          type="text"
          v-model="searchText"
          placeholder="Buscar por clave, padre, nombre, cliente, RFC..."
          @input="applyFilters"
        >
      </div>
      <div class="proyecto-extra-filters">
        <div class="proyecto-filter-field">
          <label for="FiltroFechaInicioDesde">Inicio desde</label>
          <input
            id="FiltroFechaInicioDesde"
            v-model="filters.fechaInicioDesde"
            type="date"
            class="mod-input"
            @change="applyFilters"
          >
        </div>
        <div class="proyecto-filter-field">
          <label for="FiltroFechaInicioHasta">Inicio hasta</label>
          <input
            id="FiltroFechaInicioHasta"
            v-model="filters.fechaInicioHasta"
            type="date"
            class="mod-input"
            @change="applyFilters"
          >
        </div>
        <div class="proyecto-filter-field proyecto-filter-status">
          <label for="FiltroEstatus">Estatus</label>
          <select
            id="FiltroEstatus"
            v-model="filters.estatus"
            class="mod-select proyecto-status-select"
            @change="applyFilters"
          >
            <option value="">Todos</option>
            <option
              v-for="estatus in estatusOptions"
              :key="estatus.Clave"
              :value="estatus.Clave"
            >
              {{ estatus.Nombre || estatus.Clave }}
            </option>
          </select>
        </div>
        <div class="proyecto-filter-field proyecto-filter-status">
          <label for="FiltroActivo">Activo</label>
          <select
            id="FiltroActivo"
            v-model="filters.activo"
            class="mod-select proyecto-status-select"
            @change="applyFilters"
          >
            <option value="">Todos</option>
            <option value="1">Activos</option>
            <option value="0">Inactivos</option>
          </select>
        </div>
        <button
          v-if="hasAdvancedFilters"
          type="button"
          class="mod-btn mod-btn-outline proyecto-clear-filters"
          @click="clearAdvancedFilters"
        >
          <i class="bi bi-x-circle"></i>
          <span>Limpiar</span>
        </button>
      </div>
      <div class="mod-filter-right">
        <select v-model="pageSize" @change="applyFilters" class="mod-select">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="mod-filter-count">{{ totalFiltered }} registro{{ totalFiltered !== 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- Tabla -->
    <div class="mod-table-wrap">
      <table class="mod-table">
        <thead>
          <tr>
            <th class="mod-th-sort" @click="sortBy('Clave')">
              Clave
              <i :class="sortIcon('Clave')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('ClavePadre')">
              Padre
              <i :class="sortIcon('ClavePadre')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('Nombre')">
              Nombre
              <i :class="sortIcon('Nombre')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('SocioNegocioNombre')">
              Cliente
              <i :class="sortIcon('SocioNegocioNombre')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('Responsable')">
              Responsable
              <i :class="sortIcon('Responsable')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('Estatus')">
              Estatus
              <i :class="sortIcon('Estatus')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('FechaInicio')">
              Inicio
              <i :class="sortIcon('FechaInicio')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('FechaFin')">
              Fin
              <i :class="sortIcon('FechaFin')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('PorcentajeAvance')">
              % Avance
              <i :class="sortIcon('PorcentajeAvance')"></i>
            </th>
            <th class="mod-th-sort" @click="sortBy('EsActivo')">
              Activo
              <i :class="sortIcon('EsActivo')"></i>
            </th>
            <th class="mod-th-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="11" class="mod-td-center">
              <div class="mod-loading"><span class="mod-spinner"></span> Cargando...</div>
            </td>
          </tr>
          <tr v-else-if="pagedItems.length === 0">
            <td colspan="11" class="mod-td-center mod-td-empty">
              <i class="bi bi-inbox"></i>
              <span>{{ hasAnyFilter ? 'Sin resultados' : 'No hay proyectos registrados' }}</span>
            </td>
          </tr>
          <tr
            v-for="item in pagedItems"
            :key="item.Clave + '-' + item.EmpresaId"
            class="mod-tr"
            :class="{ 'proyecto-overdue': isProjectOverdue(item) }"
          >
            <td><span class="mod-badge">{{ item.Clave }}</span></td>
            <td class="mod-td-muted">{{ item.ClavePadre || '-' }}</td>
            <td>{{ item.Nombre }}</td>
            <td>{{ item.SocioNegocioNombre || '-' }}</td>
            <td>{{ item.Responsable || '-' }}</td>
            <td class="mod-td-muted">{{ item.Estatus || '-' }}</td>
            <td class="mod-td-muted">{{ formatDate(item.FechaInicio) }}</td>
            <td>
              <span :class="isProjectOverdue(item) ? 'proyecto-overdue-date' : 'mod-td-muted'">
                {{ formatDate(item.FechaFin) }}
              </span>
            </td>
            <td>{{ item.PorcentajeAvance }}%</td>
            <td>
              <span :class="item.EsActivo ? 'mod-status-active' : 'mod-status-inactive'">
                {{ item.EsActivo ? 'Si' : 'No' }}
              </span>
            </td>
            <td class="mod-td-actions">
              <button class="mod-btn-icon" title="Editar" @click="openForm(item)">
                <i class="bi bi-pencil"></i>
              </button>
              <button class="mod-btn-icon mod-btn-danger" title="Eliminar" @click="confirmDelete(item)">
                <i class="bi bi-trash3"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginacion -->
    <div class="mod-pagination" v-if="totalPages > 1">
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
        <i class="bi bi-chevron-double-left"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === 1" @click="currentPage--">
        <i class="bi bi-chevron-left"></i>
      </button>
      <template v-for="p in visiblePages" :key="p">
        <button
          class="mod-page-btn"
          :class="{ 'mod-page-active': p === currentPage }"
          @click="currentPage = p"
        >{{ p }}</button>
      </template>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
        <i class="bi bi-chevron-right"></i>
      </button>
      <button class="mod-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
        <i class="bi bi-chevron-double-right"></i>
      </button>
      <span class="mod-page-info">Pagina {{ currentPage }} de {{ totalPages }}</span>
    </div>

    <!-- Modal Form -->
    <ProyectoForm
      v-if="showForm"
      :item="selectedItem"
      :is-edit="isEdit"
      @saved="onSaved"
      @close="showForm = false"
    />
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import proyectoService from './proyecto.service';
import proyectoEstatusService from '../proyectoestatus/proyectoestatus.service';
import authService from '@/services/auth';
import { showToast } from '@/shared/utils/toast';
import ProyectoForm from './ProyectoForm.vue';

export default {
  name: 'ProyectoList',
  components: { ProyectoForm },
  props: {
    titulo: { type: String, default: 'Proyectos' },
    subtitulo: { type: String, default: 'Catalogo de proyectos' }
  },
  data() {
    return {
      items: [],
      filteredItems: [],
      estatusOptions: [],
      searchText: '',
      filters: {
        fechaInicioDesde: '',
        fechaInicioHasta: '',
        estatus: '',
        activo: ''
      },
      loading: false,
      syncing: false,
      showForm: false,
      selectedItem: null,
      isEdit: false,
      // Paginacion
      currentPage: 1,
      pageSize: 25,
      // Ordenamiento
      sortField: 'Clave',
      sortAsc: true
    };
  },
  computed: {
    hasAdvancedFilters() {
      return Boolean(
        this.filters.fechaInicioDesde ||
        this.filters.fechaInicioHasta ||
        this.filters.estatus ||
        this.filters.activo
      );
    },
    hasAnyFilter() {
      return Boolean(this.searchText.trim()) || this.hasAdvancedFilters;
    },
    totalFiltered() {
      return this.filteredItems.length;
    },
    totalPages() {
      return Math.ceil(this.totalFiltered / this.pageSize) || 1;
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredItems.slice(start, start + this.pageSize);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  watch: {
    currentPage(val) {
      if (val > this.totalPages) this.currentPage = this.totalPages;
    },
    filters: {
      deep: true,
      handler() {
        this.applyFilters();
      }
    }
  },
  created() {
    this.loadItems();
    this.loadEstatusOptions();
  },
  methods: {
    async loadEstatusOptions() {
      try {
        const empresa = authService.getSelectedEmpresa();
        this.estatusOptions = await proyectoEstatusService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
      } catch {
        this.estatusOptions = [];
      }
    },
    async loadItems() {
      this.loading = true;
      try {
        const empresa = authService.getSelectedEmpresa();
        const result = await proyectoService.listar({ EmpresaId: empresa?.EmpresaId || 0 });
        if (Array.isArray(result)) {
          this.items = result;
        } else if (result.OData) {
          this.items = result.OData;
        } else {
          this.items = [];
        }
        this.applyFilters();
      } catch (e) {
        console.error('Error al cargar proyectos:', e);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      let data = [...this.items];

      // Busqueda
      const q = this.searchText.toLowerCase().trim();
      if (q) {
        data = data.filter(i =>
          (i.Clave || '').toLowerCase().includes(q) ||
          (i.ClavePadre || '').toLowerCase().includes(q) ||
          (i.Nombre || '').toLowerCase().includes(q) ||
          (i.SocioNegocioNombre || '').toLowerCase().includes(q) ||
          (i.SocioNegocioClaveFiscal || '').toLowerCase().includes(q) ||
          (i.Responsable || '').toLowerCase().includes(q) ||
          (i.Estatus || '').toLowerCase().includes(q)
        );
      }

      if (this.filters.fechaInicioDesde) {
        data = data.filter(i => this.dateKey(i.FechaInicio) >= this.filters.fechaInicioDesde);
      }

      if (this.filters.fechaInicioHasta) {
        data = data.filter(i => this.dateKey(i.FechaInicio) <= this.filters.fechaInicioHasta);
      }

      if (this.filters.estatus) {
        const estatus = String(this.filters.estatus).toLowerCase().trim();
        data = data.filter(i => String(i.Estatus || '').toLowerCase().trim() === estatus);
      }

      if (this.filters.activo !== '') {
        const activo = this.filters.activo === '1';
        data = data.filter(i => !!i.EsActivo === activo);
      }

      // Ordenamiento
      data.sort((a, b) => {
        let va = a[this.sortField];
        let vb = b[this.sortField];
        if (va == null) va = '';
        if (vb == null) vb = '';
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va < vb) return this.sortAsc ? -1 : 1;
        if (va > vb) return this.sortAsc ? 1 : -1;
        return 0;
      });

      this.filteredItems = data;
      this.currentPage = 1;
    },
    clearAdvancedFilters() {
      this.filters = {
        fechaInicioDesde: '',
        fechaInicioHasta: '',
        estatus: '',
        activo: ''
      };
      this.applyFilters();
    },
    dateKey(value) {
      if (!value) return '';
      return String(value).slice(0, 10);
    },
    todayKey() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    isProjectFinished(item) {
      const avance = Number(item.PorcentajeAvance || 0);
      const estatus = String(item.Estatus || '').toLowerCase();
      return avance >= 100 || /(termin|finaliz|cerrad|complet)/.test(estatus);
    },
    isProjectOverdue(item) {
      const fechaFin = this.dateKey(item.FechaFin);
      return Boolean(fechaFin && fechaFin < this.todayKey() && !this.isProjectFinished(item));
    },
    formatDate(value) {
      const key = this.dateKey(value);
      if (!key) return '-';
      const [year, month, day] = key.split('-');
      if (!year || !month || !day) return key;
      return `${day}/${month}/${year}`;
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortField = field;
        this.sortAsc = true;
      }
      this.applyFilters();
    },
    sortIcon(field) {
      if (this.sortField !== field) return 'bi bi-chevron-expand mod-sort-inactive';
      return this.sortAsc ? 'bi bi-chevron-up' : 'bi bi-chevron-down';
    },
    openForm(item = null) {
      this.isEdit = !!item;
      this.selectedItem = item ? { ...item } : null;
      this.showForm = true;
    },
    onSaved(payload = null) {
      if (payload?.keepOpen) {
        this.isEdit = true;
        this.selectedItem = payload.item ? { ...payload.item } : this.selectedItem;
        this.loadItems();
        return;
      }
      this.showForm = false;
      this.loadItems();
    },
    async confirmDelete(item) {
      if (!confirm(`Eliminar proyecto "${item.Nombre}"?`)) return;
      try {
        const result = await proyectoService.borrar(item);
        if (result.Number >= 0) {
          this.loadItems();
        } else {
          alert(result.Message || 'Error al eliminar');
        }
      } catch (e) {
        console.error('Error al eliminar:', e);
        alert('Error al eliminar');
      }
    },
    async sincronizarErp() {
      this.syncing = true;
      try {
        const result = await proyectoService.sincronizarErp();
        if (result.Number >= 0) {
          showToast(result.Message || 'Sincronización completada', 'success');
          this.loadItems();
        } else {
          showToast(result.Message || 'Error en sincronización', 'error');
        }
      } catch (e) {
        console.error('Error al sincronizar:', e);
        showToast('Error de conexión al sincronizar', 'error');
      } finally {
        this.syncing = false;
      }
    },
    exportExcel() {
      const data = this.filteredItems.map(i => ({
        Clave: i.Clave,
        Padre: i.ClavePadre || '',
        Nombre: i.Nombre,
        Cliente: i.SocioNegocioNombre || '',
        RFC: i.SocioNegocioClaveFiscal || '',
        Responsable: i.Responsable || '',
        Estatus: i.Estatus || '',
        'Fecha inicio': this.formatDate(i.FechaInicio),
        'Fecha fin': this.formatDate(i.FechaFin),
        '% Avance': i.PorcentajeAvance,
        Activo: i.EsActivo ? 'Si' : 'No'
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      // Auto-width
      const colWidths = Object.keys(data[0] || {}).map(k => ({
        wch: Math.max(k.length, ...data.map(r => String(r[k] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Proyectos');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'proyectos.xlsx');
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

.proyecto-page {
  width: 100%;
  max-width: none;
}

.proyecto-page .mod-table {
  min-width: 1400px;
}

.proyecto-overdue {
  background: #fff1f2;
}

.proyecto-overdue:hover {
  background: #ffe4e6;
}

.proyecto-overdue-date {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.55rem;
  border: 1px solid #fb7185;
  border-radius: 999px;
  background: #be123c;
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.proyecto-filters {
  align-items: flex-end;
  gap: 0.75rem;
}

.proyecto-filters .mod-search {
  height: 40px;
  margin-bottom: 0;
}

.proyecto-extra-filters {
  display: flex;
  align-items: end;
  gap: 0.75rem;
  flex-wrap: wrap;
  flex: 1 1 auto;
}

.proyecto-filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 150px;
}

.proyecto-filter-field label {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.proyecto-filter-field input[type="date"],
.proyecto-status-select {
  height: 40px;
  min-height: 40px;
  padding: 0.45rem 0.65rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #2d3748;
  font-family: inherit;
  font-size: 0.85rem;
  line-height: 1.2;
}

.proyecto-filter-field input[type="date"]:focus,
.proyecto-status-select:focus {
  outline: none;
  border-color: var(--t-border-focus, #1272c4);
  box-shadow: 0 0 0 3px var(--t-primary-ring, rgba(18, 114, 196, 0.1));
}

.proyecto-filter-status {
  width: 220px;
}

.proyecto-status-select {
  width: 100%;
}

.proyecto-clear-filters {
  height: 40px;
  white-space: nowrap;
}

.proyecto-filters .mod-filter-right {
  height: 40px;
  margin-left: 0;
}

@media (max-width: 900px) {
  .proyecto-filters {
    align-items: stretch;
  }

  .proyecto-filters .mod-search,
  .proyecto-extra-filters,
  .proyecto-filter-field,
  .proyecto-filter-status {
    width: 100%;
    max-width: none;
  }

  .proyecto-filters .mod-filter-right {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
